import { Box, Typography } from "@avenue-8/sidekick-ui";
import { PresentationSectionRenderer } from "./presentation-sections/PresentationSectionRenderer";
import {
  ComparableProperty,
  PresentationEditData,
  PropertyPhoto,
  SectionView,
} from "@cma/presentation-generation-logic";
import { Footer } from "./presentation-sections/Footer";
import { AddSectionButton } from "./shared/AddSectionButton";
import {
  PresentationSectionOnChangeHandler,
  PresentationSectionOnDeleteHandler,
} from "./presentation-sections/PresentationSectionProps";
import { AddPresentationSectionActions } from "./usePresentationLogic";
import { SectionConfig } from "@cma/presentation-generation-logic";
import { sectionWidgetRegistry } from "@cma/presentation-generation-logic/sections/section-widget-registry";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  SortablePresentationSection,
  SectionFooterWrapper,
} from "./SortablePresentationSection";
import { useState } from "react";
import { createPortal } from "react-dom";
import styled from "@emotion/styled";
import { PresentationSectionSidebarAction } from "./PresentationSectionSidebar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const HEADER_SECTION_TYPES: SectionConfig["type"][] = [
  "header",
  "header-branding",
  "general-header",
];

type SidebarActionType = "edit" | "delete" | "visibility";

const SECTION_SIDEBAR_ACTIONS: Partial<
  Record<SectionConfig["type"], SidebarActionType[]>
> = {
  "free-html-text": ["edit", "delete"],
  "ai-assisted-text": ["edit", "delete"],
  branding: ["delete"],
  "team-branding": ["delete"],
  insights: ["edit"],
};

const DragOverlayCard = styled(Box)`
  background-color: ${(p) => p.theme.colors.background};
  border: 2px dashed ${(p) => p.theme.colors.border2};
  border-radius: 0.5rem;
  padding: 1.5rem 2rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  cursor: grabbing;
  min-width: 200px;
`;

interface Props {
  displayMode: "mobile" | "desktop" | "print" | "tablet";
  editMode?: boolean;
  presentation: PresentationEditData;
  addSectionActions: AddPresentationSectionActions;
  onUpdateSection: PresentationSectionOnChangeHandler;
  onDeleteSection: PresentationSectionOnDeleteHandler;
  onReorderSections: (oldIndex: number, newIndex: number) => void;
  onOpenFindComparablePropertiesWidget: () => void;
  // onFetchSectionsData?: () => void;
  isFetchingAllSectionsData?: boolean;
  sections: SectionView[];
  galleries: { [key: string]: PropertyPhoto[] };
  updatedAt: Date;
  handleRemoveComparableProperty: (
    comparableProperty: ComparableProperty,
  ) => void;
  handleUpdateComparableProperty: (
    comparableProperty: ComparableProperty,
  ) => void;
  handleReorderComparableProperties: (
    comparableProperties: ComparableProperty[],
  ) => void;
}

export const PresentationEditor = (props: Props) => {
  const {
    presentation,
    sections,
    displayMode,
    galleries,
    editMode,
    updatedAt,
    addSectionActions,
    onUpdateSection,
    onDeleteSection,
    onReorderSections,
    onOpenFindComparablePropertiesWidget,
    // onFetchSectionsData,
    isFetchingAllSectionsData,
    handleRemoveComparableProperty,
    handleUpdateComparableProperty,
    handleReorderComparableProperties,
  } = props;

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveSectionId(active.id as string);
  };

  const sortableSectionIds = sections
    .filter((s) => {
      const section = presentation.sections.find((p) => p.config.id === s.id);
      return section && !HEADER_SECTION_TYPES.includes(section.config.type);
    })
    .map((s) => s.id);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveSectionId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex((s) => s.id === active.id);
    const newIndex = sections.findIndex((s) => s.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      onReorderSections(oldIndex, newIndex);
    }
  };

  const ACTION_FACTORIES: Record<
    SidebarActionType,
    (sectionConfig: SectionConfig) => PresentationSectionSidebarAction
  > = {
    edit: (sectionConfig) => ({
      icon: <EditIcon fontSize="small" />,
      tooltip: "Edit",
      onClick: () => setEditingSectionId(sectionConfig.id),
    }),
    delete: (sectionConfig) => ({
      icon: <DeleteOutlineOutlinedIcon fontSize="small" />,
      tooltip: "Delete",
      onClick: () => onDeleteSection(sectionConfig.id),
    }),
    visibility: (sectionConfig) => {
      const isVisible = sectionConfig.visibility !== false;
      return {
        icon: isVisible ? (
          <VisibilityOffIcon fontSize="small" />
        ) : (
          <VisibilityIcon fontSize="small" />
        ),
        tooltip: isVisible ? "Hide section" : "Show section",
        onClick: () =>
          onUpdateSection({
            sectionConfig: { ...sectionConfig, visibility: !isVisible },
          }),
      };
    },
  };

  const buildQuickActions = (
    sectionConfig: SectionConfig,
  ): PresentationSectionSidebarAction[] => [
    ...(SECTION_SIDEBAR_ACTIONS[sectionConfig.type] ?? []).map((actionType) =>
      ACTION_FACTORIES[actionType](sectionConfig),
    ),
    ACTION_FACTORIES.visibility(sectionConfig),
  ];

  const activeSectionView = sections.find((s) => s.id === activeSectionId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortableSectionIds}
        strategy={verticalListSortingStrategy}
      >
        <Box>
          {sections.map((sectionView, index) => {
            const presentationSection = presentation.sections.find(
              (s) => s.config.id === sectionView.id,
            );

            if (!presentationSection) {
              return null;
            }

            const isHeader = HEADER_SECTION_TYPES.includes(
              presentationSection.config.type,
            );

            const widget = sectionWidgetRegistry.get(
              presentationSection.config.type,
            );
            const sectionCanFetch =
              !!widget?.canFetchData &&
              widget.canFetchData({
                config: presentationSection.config,
                presentationSourceData: presentation.sourceData,
              });
            const isPending =
              sectionCanFetch &&
              (isFetchingAllSectionsData ||
                (presentationSection.data as { fetchedAt?: Date | null } | null)
                  ?.fetchedAt == null);

            const sectionRenderer = (
              <PresentationSectionRenderer
                sectionConfig={presentationSection.config}
                sectionData={presentationSection.data ?? undefined}
                sourceData={presentation.sourceData}
                onUpdateSection={onUpdateSection}
                onDeleteSection={onDeleteSection}
                onOpenFindComparablePropertiesWidget={
                  onOpenFindComparablePropertiesWidget
                }
                // onFetchSectionsData={onFetchSectionsData}
                sectionView={sectionView}
                displayMode={displayMode}
                galleries={galleries}
                editMode={editMode}
                handleRemoveComparableProperty={handleRemoveComparableProperty}
                handleUpdateComparableProperty={handleUpdateComparableProperty}
                handleReorderComparableProperties={
                  handleReorderComparableProperties
                }
                isEditing={editingSectionId === presentationSection.config.id}
                onIsEditingChange={(isEditing) =>
                  setEditingSectionId(
                    isEditing ? presentationSection.config.id : null,
                  )
                }
                isPending={isPending}
              />
            );

            const addSectionButton = (
              <AddSectionButton
                addSectionActions={addSectionActions}
                insertAfterIndex={index}
              />
            );

            if (isHeader) {
              return (
                <div key={sectionView.id} style={{ position: "relative" }}>
                  {sectionRenderer}
                  <SectionFooterWrapper>
                    {addSectionButton}
                  </SectionFooterWrapper>
                </div>
              );
            }

            return (
              <SortablePresentationSection
                key={sectionView.id}
                id={sectionView.id}
                footer={addSectionButton}
                isHidden={presentationSection.config.visibility === false}
                quickActions={buildQuickActions(presentationSection.config)}
              >
                {sectionRenderer}
              </SortablePresentationSection>
            );
          })}
          <Footer updatedAt={updatedAt} />
        </Box>
      </SortableContext>

      {createPortal(
        <DragOverlay>
          {activeSectionView && (
            <DragOverlayCard>
              <Typography variant="body2" color="text.secondary">
                {activeSectionView.navTitle ||
                  activeSectionView.title ||
                  "Section"}
              </Typography>
            </DragOverlayCard>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
